import type { WordSequenceMetadataDto } from '@faro/metadata-angular-client';

/** Maximum number of visible characters of the "Thema" tooltip; longer content is cut with an ellipsis. */
export const TOPIC_TOOLTIP_MAX_LENGTH = 1800;

/**
 * Composes the tooltip HTML for the "Thema" column of a TOPIC hit from metadata service data:
 * - a "Sequenzen:" section built from the sequences description text (Item.SEQ) of the item,
 * - followed by a "Wortsequenzen:" section built from the word sequences of the item,
 *   formatted analogous to a word sequence hit on the WORD hitlist.
 * Empty sections are omitted.
 */
export function composeTopicTooltip(sequencesText: string, wordSequences: WordSequenceMetadataDto[]): string {
    const sections: string[] = [];

    if (sequencesText.trim()) {
        sections.push(sequencesSection(sequencesText));
    }

    if (wordSequences.length > 0) {
        sections.push(wordSequencesSection(wordSequences));
    }

    return truncateHtmlToTextLength(sections.join(''), TOPIC_TOOLTIP_MAX_LENGTH);
}

function sequencesSection(sequencesText: string): string {
    return `<div class="sequences-section"><b>Sequenzen:</b><br>${toHtmlText(sequencesText.trim())}</div>`;
}

function wordSequencesSection(wordSequences: WordSequenceMetadataDto[]): string {
    const blocks = wordSequences
        .map(ws => {
            // speaker, validation state and context share one (light grey) header line
            // and are rendered as a tag like on the WORD hitlist
            const headerParts: string[] = [];
            if (ws.speaker) {
                const validationSuffix = ws.isSpeakerValidated ? '' : ' (nicht validiert)';
                headerParts.push(`<span class="word-sequence-speaker">${toHtmlText(ws.speaker)}${validationSuffix}</span>`);
            }
            if (ws.context) {
                headerParts.push(`<span>Kontext:&nbsp;${toHtmlText(ws.context)}</span>`);
            }

            const lines: string[] = [];
            if (headerParts.length > 0) {
                lines.push(`<div class="word-sequence-header">${headerParts.join('')}</div>`);
            }
            if (ws.text) {
                lines.push(`<div>${toHtmlText(ws.text)}</div>`);
            }
            return lines;
        })
        .filter(lines => lines.length > 0)
        .map(lines => `<div class="word-sequence">${lines.join('')}</div>`);

    return `<div class="word-sequences-section"><b>Wortsequenzen:</b>${blocks.join('')}</div>`;
}

/** Escapes HTML and converts newlines to line breaks. */
function toHtmlText(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/\r?\n/g, '<br>');
}

/**
 * Truncates HTML so that its visible text is at most maxLength characters, appending an
 * ellipsis at the cut. Markup is preserved; everything after the cut is removed.
 */
export function truncateHtmlToTextLength(html: string, maxLength: number): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);

    let remaining = maxLength;
    let truncationNode: Text | null = null;
    let node: Node | null;
    while ((node = walker.nextNode())) {
        const textNode = node as Text;
        if (textNode.data.length <= remaining) {
            remaining -= textNode.data.length;
        } else {
            textNode.data = textNode.data.substring(0, remaining) + '…';
            truncationNode = textNode;
            break;
        }
    }

    if (!truncationNode) {
        return html;
    }

    // remove everything after the truncation point in document order
    let current: Node = truncationNode;
    while (current !== doc.body) {
        while (current.nextSibling) {
            current.nextSibling.remove();
        }
        current = current.parentNode!;
    }

    return doc.body.innerHTML;
}
